import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { SortingState } from "@tanstack/react-table";
import { ChevronLeft, ListMusic } from "lucide-react";
import { BoundedFilterSchema } from "#shared/schemas/filter.ts";
import { ActionBar } from "@web/components/ActionBar.tsx";
import { NamePromptDialog } from "@web/components/ConfirmDialog.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import {
  useAlbumBrowse,
  useArtists,
  useCreateLocalPlaylist,
  useCreateSavedView,
  useGenresFlat,
  useLocalPlaylists,
  usePlaylists,
  useSavedViews,
  useTracksInfinite,
} from "@web/features/library/api.ts";
import { useBulkPlaylist } from "@web/features/library/bulkApi.ts";
import { FilterBar } from "@web/features/library/FilterBar.tsx";
import {
  browseFilterAst,
  buildBrowseFilter,
  formatIdList,
  parseIdList,
} from "@web/features/library/filterUrl.ts";
import { useReorderLocalPlaylistTracks } from "@web/features/library/reorderApi.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { useSelectionStore } from "@web/stores/selection.ts";
import type { DetailTarget } from "@web/stores/detail.ts";
import { buildTrackColumns } from "@web/features/grid/columns.tsx";
import { DataGrid } from "@web/features/grid/DataGrid.tsx";
import { toast } from "sonner";

export type LibraryKind = "liked" | "all" | "playlist" | "local" | "view";

/** Track IDs from a graph-path “Open tracks in grid” filter (`track.id in [...]`). */
function trackIdsFromGraphFilter(rawFilter: string | null): string[] | null {
  if (!rawFilter) return null;
  try {
    const parsed: unknown = JSON.parse(rawFilter);
    if (
      parsed &&
      typeof parsed === "object" &&
      "field" in parsed &&
      "cmp" in parsed &&
      "value" in parsed &&
      (parsed as { field: unknown }).field === "track.id" &&
      (parsed as { cmp: unknown }).cmp === "in" &&
      Array.isArray((parsed as { value: unknown }).value)
    ) {
      const ids = (parsed as { value: unknown[] }).value.filter(
        (v): v is string => typeof v === "string" && v.length > 0,
      );
      return ids.length > 0 ? ids : null;
    }
  } catch {
    return null;
  }
  return null;
}

function scopeFromRoute(kind: LibraryKind, id?: string): string {
  switch (kind) {
    case "liked":
      return "liked";
    case "all":
      return "all";
    case "playlist":
      return `playlist:${id ?? ""}`;
    case "local":
      return `local:${id ?? ""}`;
    case "view":
      return "all";
  }
}

function sortParam(sorting: SortingState): string | undefined {
  if (sorting.length === 0) return undefined;
  return sorting.map((s) => `${s.id}:${s.desc ? "desc" : "asc"}`).join(",");
}

/** What the current route is *about*, so the sheet can describe the scope itself. */
function scopeTarget(
  kind: LibraryKind,
  id: string | undefined,
  artistIds: readonly string[],
  albumIds: readonly string[],
  genreIds: readonly string[],
): { target: DetailTarget; label: string } | null {
  if (kind === "playlist" && id) return { target: { type: "playlist", id }, label: "Playlist" };
  if (kind === "local" && id) return { target: { type: "local_playlist", id }, label: "Playlist" };
  if (artistIds.length === 1) {
    return { target: { type: "artist", id: artistIds[0]! }, label: "Artist" };
  }
  if (albumIds.length === 1) {
    return { target: { type: "album", id: albumIds[0]! }, label: "Album" };
  }
  if (genreIds.length === 1) {
    return { target: { type: "genre", id: genreIds[0]! }, label: "Genre" };
  }
  return null;
}

function patchIdParam(
  next: URLSearchParams,
  key: string,
  ids: readonly string[],
) {
  const formatted = formatIdList(ids);
  if (formatted) next.set(key, formatted);
  else next.delete(key);
}

export function LibraryPage({ kind }: { kind: LibraryKind }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const artistIds = useMemo(
    () => parseIdList(searchParams.get("artist")),
    [searchParams],
  );
  const albumIds = useMemo(
    () => parseIdList(searchParams.get("album")),
    [searchParams],
  );
  const genreIds = useMemo(
    () => parseIdList(searchParams.get("genre")),
    [searchParams],
  );
  const assignGenreId = searchParams.get("assignGenre");
  const rawFilter = searchParams.get("filter");
  const [draftQ, setDraftQ] = useState(qParam);
  const density = useUiStore((s) => s.density);
  const setDensity = useUiStore((s) => s.setDensity);
  const clearSelection = useSelectionStore((s) => s.clear);
  const selectAllFiltered = useSelectionStore((s) => s.selectAllFiltered);
  const selectionCount = useSelectionStore((s) => s.count());
  const selectedIds = useSelectionStore((s) => s.selectedIds);
  const bulkTarget = useSelectionStore((s) => s.bulkTarget);
  const openDetail = useOpenDetail();
  const scopeEntity = scopeTarget(kind, id, artistIds, albumIds, genreIds);

  useEffect(() => {
    setDraftQ(qParam);
  }, [qParam]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (draftQ.trim()) next.set("q", draftQ.trim());
      else next.delete("q");
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [draftQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (!assignGenreId) return;
    setSorting([{ id: "track.added_at", desc: true }]);
  }, [assignGenreId]);

  const scope = scopeFromRoute(kind, id);
  const scopeKey = kind === "view" ? `view:${id}` : scope;
  const filterJson = useMemo(
    () =>
      buildBrowseFilter({
        artistIds,
        albumIds,
        genreIds,
        rawFilter,
      }),
    [artistIds, albumIds, genreIds, rawFilter],
  );

  const artistKey = artistIds.join(",");
  const albumKey = albumIds.join(",");
  const genreKey = genreIds.join(",");

  useEffect(() => {
    clearSelection();
  }, [scope, qParam, artistKey, albumKey, genreKey, rawFilter, clearSelection]);

  const playlists = usePlaylists();
  const local = useLocalPlaylists();
  const views = useSavedViews();
  const artists = useArtists();
  const albums = useAlbumBrowse("");
  const genres = useGenresFlat();
  const createLocalPlaylist = useCreateLocalPlaylist();
  const createSavedView = useCreateSavedView();
  const bulkAddToPlaylist = useBulkPlaylist();
  const reorderLocalTracks = useReorderLocalPlaylistTracks();
  const [savePlaylistOpen, setSavePlaylistOpen] = useState(false);
  const [newPlaylistOpen, setNewPlaylistOpen] = useState(false);

  const graphTrackIds = useMemo(() => trackIdsFromGraphFilter(rawFilter), [rawFilter]);
  /** Artist-path IDs or playlist-explorer membership filters both land as /all?filter=. */
  const isGraphSelection = kind === "all" && Boolean(rawFilter);
  const graphFilter = useMemo(() => {
    if (!rawFilter) return null;
    try {
      const parsed: unknown = JSON.parse(rawFilter);
      const result = BoundedFilterSchema.safeParse(parsed);
      return result.success ? result.data : null;
    } catch {
      return null;
    }
  }, [rawFilter]);
  const canNewPlaylistFromSelection =
    (kind === "liked" || kind === "all") && selectionCount > 0 && !assignGenreId;

  const tracks = useTracksInfinite({
    scope,
    q: qParam || undefined,
    sort: sortParam(sorting),
    filter: filterJson,
    viewId: kind === "view" ? id : undefined,
  });

  const rows = useMemo(
    () => tracks.data?.pages.flatMap((page) => page.rows) ?? [],
    [tracks.data],
  );
  const total = tracks.data?.pages[0]?.total ?? 0;
  const columns = useMemo(
    () => buildTrackColumns({ markNotLiked: kind === "all" }),
    [kind],
  );

  const artistOptions = useMemo(
    () =>
      (artists.data ?? []).map((a) => ({
        id: a.id,
        label: a.name ?? a.id,
      })),
    [artists.data],
  );
  const albumOptions = useMemo(
    () =>
      (albums.data ?? []).map((a) => ({
        id: a.id,
        label: a.name ?? a.id,
      })),
    [albums.data],
  );
  const genreOptions = useMemo(
    () =>
      (genres.data ?? []).map((g) => ({
        id: g.id,
        label: g.name,
      })),
    [genres.data],
  );

  const artistName =
    artistIds.length === 1
      ? (artists.data?.find((a) => a.id === artistIds[0])?.name ?? "Artist")
      : artistIds.length > 1
        ? `${artistIds.length} artists`
        : null;
  const albumName =
    albumIds.length === 1
      ? (albums.data?.find((a) => a.id === albumIds[0])?.name ?? "Album")
      : albumIds.length > 1
        ? `${albumIds.length} albums`
        : null;
  const genreName =
    genreIds.length === 1
      ? (genres.data?.find((g) => g.id === genreIds[0])?.name ?? "Genre")
      : genreIds.length > 1
        ? `${genreIds.length} genres`
        : assignGenreId
          ? (genres.data?.find((g) => g.id === assignGenreId)?.name ?? "Genre")
          : null;

  const title = useMemo(() => {
    if (kind === "liked") return "Liked Songs";
    if (kind === "all") {
      if (rawFilter) return "Graph selection";
      if (assignGenreId && genreName) return `Add to ${genreName}`;
      if (artistName && genreName) return `${artistName} · ${genreName}`;
      if (artistName) return artistName;
      if (albumName) return albumName;
      if (genreName && genreIds.length > 0) return genreName;
      return "All tracks";
    }
    if (kind === "playlist") {
      return playlists.data?.find((p) => p.id === id)?.name ?? "Playlist";
    }
    if (kind === "local") {
      return local.data?.find((p) => p.id === id)?.name ?? "Local playlist";
    }
    return views.data?.find((v) => v.id === id)?.name ?? "Saved view";
  }, [
    kind,
    id,
    playlists.data,
    local.data,
    views.data,
    artistName,
    albumName,
    genreName,
    rawFilter,
    assignGenreId,
    genreIds.length,
  ]);

  const hasMore = Boolean(tracks.hasNextPage);
  const loadingMore = tracks.isFetchingNextPage;
  const isFiltered =
    artistIds.length > 0 ||
    albumIds.length > 0 ||
    genreIds.length > 0 ||
    Boolean(assignGenreId) ||
    Boolean(rawFilter) ||
    Boolean(qParam.trim());

  const canSaveView =
    kind !== "view" &&
    (artistIds.length > 0 ||
      albumIds.length > 0 ||
      genreIds.length > 0 ||
      Boolean(rawFilter) ||
      Boolean(qParam.trim()));

  function setFacetIds(key: "artist" | "album" | "genre", ids: string[]) {
    const next = new URLSearchParams(searchParams);
    patchIdParam(next, key, ids);
    setSearchParams(next, { replace: true });
  }

  function clearBrowseFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete("artist");
    next.delete("album");
    next.delete("genre");
    next.delete("filter");
    next.delete("assignGenre");
    next.delete("q");
    setDraftQ("");
    setSearchParams(next, { replace: true });
  }

  function startAddTracksToGenre() {
    if (genreIds.length !== 1) return;
    void navigate(`/all?assignGenre=${encodeURIComponent(genreIds[0]!)}`);
  }

  function saveCurrentAsView(name: string) {
    const query = browseFilterAst({
      artistIds,
      albumIds,
      genreIds,
      rawFilter,
      q: qParam,
    });
    if (!query) {
      toast.error("Nothing to save");
      return;
    }
    createSavedView.mutate({ name, query });
  }

  function saveGraphSelectionAsPlaylist(name: string) {
    if (graphTrackIds && graphTrackIds.length > 0) {
      createLocalPlaylist.mutate(
        { name, track_ids: graphTrackIds },
        {
          onSuccess: () => setSavePlaylistOpen(false),
        },
      );
      return;
    }
    if (!graphFilter || total === 0) {
      toast.error("Nothing to save");
      return;
    }
    createLocalPlaylist.mutate(
      { name },
      {
        onSuccess: (playlist) => {
          bulkAddToPlaylist.mutate(
            {
              local_playlist_id: playlist.id,
              action: "add",
              target: { scope: "all", filter: graphFilter },
            },
            {
              onSuccess: () => setSavePlaylistOpen(false),
            },
          );
        },
      },
    );
  }

  function createPlaylistFromSelection(name: string) {
    const ids = selectedIds();
    if (ids && ids.length > 0) {
      createLocalPlaylist.mutate(
        { name, track_ids: ids },
        {
          onSuccess: () => {
            setNewPlaylistOpen(false);
            clearSelection();
          },
        },
      );
      return;
    }
    const target = bulkTarget();
    if (!target) {
      toast.error("Nothing selected");
      return;
    }
    createLocalPlaylist.mutate(
      { name },
      {
        onSuccess: (playlist) => {
          bulkAddToPlaylist.mutate(
            {
              local_playlist_id: playlist.id,
              action: "add",
              target,
            },
            {
              onSuccess: () => setNewPlaylistOpen(false),
            },
          );
        },
      },
    );
  }

  const extraChips = useMemo(() => {
    if (!rawFilter) return [];
    return [
      {
        key: "graph-filter",
        label: "Graph selection",
        onRemove: () => {
          const next = new URLSearchParams(searchParams);
          next.delete("filter");
          setSearchParams(next, { replace: true });
        },
      },
    ];
  }, [rawFilter, searchParams, setSearchParams]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-col gap-3 border-b border-line px-5 py-4">
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            {genreIds.length === 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-2 mb-1 h-7 gap-1 px-2 text-muted"
                onClick={() => void navigate("/genres")}
              >
                <ChevronLeft className="size-4" />
                Genres
              </Button>
            )}
            {albumIds.length === 1 && genreIds.length === 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-2 mb-1 h-7 gap-1 px-2 text-muted"
                onClick={() => void navigate("/albums")}
              >
                <ChevronLeft className="size-4" />
                Albums
              </Button>
            )}
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">
              {isFiltered ? "Filtered" : "Library"}
            </p>
            <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
            <p className="mt-1 text-sm text-muted">
              {tracks.isLoading ? "Loading…" : `${total.toLocaleString()} tracks`}
              {assignGenreId && (
                <span className="text-faint">
                  {" "}
                  · select tracks, then Genre → Add ({genreName} is preselected)
                </span>
              )}
              {kind === "all" &&
                artistIds.length === 0 &&
                genreIds.length === 0 &&
                !assignGenreId &&
                !rawFilter && (
                  <span className="text-faint"> · liked + playlist tracks</span>
                )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isGraphSelection && (
              <Button
                variant="secondary"
                size="sm"
                disabled={
                  (graphTrackIds ? graphTrackIds.length === 0 : total === 0) ||
                  createLocalPlaylist.isPending ||
                  bulkAddToPlaylist.isPending
                }
                onClick={() => setSavePlaylistOpen(true)}
              >
                <ListMusic className="size-3.5" />
                Save as playlist…
              </Button>
            )}
            {canNewPlaylistFromSelection && (
              <Button
                variant="secondary"
                size="sm"
                disabled={createLocalPlaylist.isPending || bulkAddToPlaylist.isPending}
                onClick={() => setNewPlaylistOpen(true)}
              >
                <ListMusic className="size-3.5" />
                New playlist…
              </Button>
            )}
            {genreIds.length === 1 && !assignGenreId && (
              <Button variant="secondary" size="sm" onClick={startAddTracksToGenre}>
                Add tracks…
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => selectAllFiltered(scope, qParam, total)}
              disabled={total === 0}
            >
              Select all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDensity(density === "compact" ? "comfortable" : "compact")}
            >
              {density === "compact" ? "Comfortable" : "Compact"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!scopeEntity}
              onClick={() => {
                if (!scopeEntity) return;
                openDetail(scopeEntity.target);
              }}
            >
              Details
            </Button>
          </div>
        </div>

        {kind !== "view" && (
          <FilterBar
            searchValue={draftQ}
            onSearchChange={setDraftQ}
            searchPlaceholder="Search in this view"
            artistOptions={artistOptions}
            albumOptions={albumOptions}
            genreOptions={genreOptions}
            artistIds={artistIds}
            albumIds={albumIds}
            genreIds={genreIds}
            onArtistIdsChange={(ids) => setFacetIds("artist", ids)}
            onAlbumIdsChange={(ids) => setFacetIds("album", ids)}
            onGenreIdsChange={(ids) => setFacetIds("genre", ids)}
            extraChips={extraChips}
            onClearAll={clearBrowseFilters}
            saveAsView={
              canSaveView
                ? { busy: createSavedView.isPending, onSave: saveCurrentAsView }
                : undefined
            }
          />
        )}
        {kind === "view" && (
          <FilterBar
            searchValue={draftQ}
            onSearchChange={setDraftQ}
            searchPlaceholder="Search in this view"
          />
        )}
      </header>

      <ActionBar />

      <div className="min-h-0 flex-1">
        {tracks.isLoading && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 12 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        )}

        {tracks.isError && (
          <QueryErrorState
            error={tracks.error}
            onRetry={() => void tracks.refetch()}
            actionLabel="load tracks"
          />
        )}

        {tracks.isSuccess && rows.length === 0 && (
          <EmptyState
            title={isFiltered ? "No matches" : "Nothing here"}
            description={
              isFiltered
                ? "Try a different search, or clear the filter."
                : "This scope has no tracks yet. Sync from Spotify once quota clears, or pick another playlist."
            }
          />
        )}

        {tracks.isSuccess && rows.length > 0 && (
          <DataGrid
            rows={rows}
            columns={columns}
            scopeKey={scopeKey}
            scope={scope}
            q={qParam}
            total={total}
            sorting={sorting}
            onSortingChange={setSorting}
            onReorder={
              kind === "local" && id
                ? (event) => {
                    reorderLocalTracks.mutate({
                      playlistId: id,
                      trackIds: event.trackIds,
                      overTrackId: event.overTrackId,
                      edge: event.edge,
                    });
                  }
                : undefined
            }
            hasMore={hasMore}
            loadingMore={loadingMore}
            onReachEnd={() => {
              if (hasMore && !loadingMore) void tracks.fetchNextPage();
            }}
          />
        )}
      </div>

      <NamePromptDialog
        open={savePlaylistOpen}
        title="Save as playlist"
        description={`Create a local playlist with ${(graphTrackIds?.length ?? total).toLocaleString()} tracks from this graph selection.`}
        initialValue={`Graph selection · ${graphTrackIds?.length ?? total}`}
        confirmLabel="Create"
        busy={createLocalPlaylist.isPending || bulkAddToPlaylist.isPending}
        onCancel={() => setSavePlaylistOpen(false)}
        onConfirm={saveGraphSelectionAsPlaylist}
      />

      <NamePromptDialog
        open={newPlaylistOpen}
        title="New playlist"
        description={`Create a local playlist with ${selectionCount.toLocaleString()} selected track${selectionCount === 1 ? "" : "s"}.`}
        initialValue={`Selection · ${selectionCount}`}
        confirmLabel="Create"
        busy={createLocalPlaylist.isPending || bulkAddToPlaylist.isPending}
        onCancel={() => setNewPlaylistOpen(false)}
        onConfirm={createPlaylistFromSelection}
      />
    </div>
  );
}
